import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useProgressStore } from '../state/progressStore.ts';
import { Button, Modal } from '../ui/index.ts';
import { Briefing } from './phases/Briefing.tsx';
import { Heist } from './phases/Heist.tsx';
import { Hideout } from './phases/Hideout.tsx';
import { Planning } from './phases/Planning.tsx';
import { ApprovalStamp } from './fx/ApprovalStamp.tsx';
import { PhaseTransition } from './fx/PhaseTransition.tsx';
import { Reveal } from './phases/Reveal.tsx';
import { GameProvider, useGame } from './GameContext.tsx';
import { useGameStore, useGameView } from './gameStore.ts';
import { usePadStore } from './padStore.ts';
import { useAutoPause } from './useAutoPause.ts';
import { useGameAudio } from './useGameAudio.ts';
import { useGameKeys } from './useGameKeys.ts';
import { HudBar } from './hud/HudBar.tsx';
import { useHeistAnalytics } from '../analytics/useHeistAnalytics.ts';
import { ReplayLayer, ReplaySpace } from '../replay/ReplayLayer.tsx';
import { ReplayClient } from '../replay/replayClient.ts';
import { OnlineLayer } from './OnlineLayer.tsx';
import { ChapterComplete } from './tutorial/ChapterComplete.tsx';
import { Coach } from './tutorial/Coach.tsx';
import styles from './GameScreen.module.css';

function PhaseBody() {
  const { view } = useGame();
  switch (view.game.phase) {
    case 'briefing':
      return <Briefing />;
    case 'planning':
      return <Planning />;
    case 'execution':
    case 'escape':
      return <Heist />;
    case 'hideout':
      return <Hideout />;
    case 'reveal':
    case 'ended':
      return <Reveal />;
  }
}

function GameLayout() {
  const { t } = useTranslation('game');
  const { t: tOnline } = useTranslation('online');
  const { client, view } = useGame();
  const navigate = useNavigate();
  const leave = useGameStore((s) => s.leave);
  const [leaving, setLeaving] = useState(false);
  const over = view.game.phase === 'reveal' || view.game.phase === 'ended';

  // A new phase starts at the top of the page, not where the last one was scrolled to.
  const phase = view.game.phase;
  const substage = view.planning?.substage ?? null;
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [phase, substage]);

  // Every game starts with the phrase pad closed.
  useEffect(() => () => usePadStore.getState().set(false), []);

  useGameAudio(view);
  useAutoPause(client, !over);
  useHeistAnalytics(client, view);
  useGameKeys(() => {
    if (client.canPause && !over) client.setPaused(!client.paused());
  });

  // Tutorial: when the lesson is learned the game waits for the player's choice.
  const chapter = client.chapter;
  const lessonDone = chapter !== null && client.lessonDone();
  const [keptOn, setKeptOn] = useState<typeof client | null>(null);
  const showComplete = lessonDone && keptOn !== client;
  const completeChapter = useProgressStore((s) => s.completeChapter);
  const chapterId = chapter?.id ?? null;
  useEffect(() => {
    if (lessonDone && chapterId !== null) completeChapter(chapterId);
  }, [lessonDone, chapterId, completeChapter]);
  useEffect(() => {
    if (showComplete) client.setPaused(true);
  }, [showComplete, client]);

  const online = client.mode === 'online';
  const replay = client instanceof ReplayClient ? client : null;
  const home = useGameStore((s) => s.home);
  const quit = () => {
    leave();
    void navigate(home);
  };

  return (
    <div className={styles['screen']} data-replay={replay !== null}>
      <HudBar
        onLeave={() => {
          if (over || replay) quit();
          else {
            if (client.canPause) client.setPaused(true);
            setLeaving(true);
          }
        }}
        onPause={() => client.setPaused(!client.paused())}
      />
      <main id="main" tabIndex={-1} className={styles['body']}>
        {chapter ? <Coach chapter={chapter} /> : null}
        <PhaseBody />
        {replay ? <ReplaySpace /> : null}
      </main>
      {online ? <OnlineLayer /> : null}
      {replay ? <ReplayLayer client={replay} /> : null}
      <ApprovalStamp />
      <PhaseTransition phase={phase} />

      {chapter ? (
        <ChapterComplete
          chapter={chapter}
          open={showComplete}
          onKeep={() => {
            setKeptOn(client);
            client.setPaused(false);
          }}
        />
      ) : null}
      <Modal
        open={client.paused() && !leaving && !showComplete}
        onClose={() => client.setPaused(false)}
        title={t('pause.title')}
        actions={
          <>
            <Button variant="ghost" onClick={() => setLeaving(true)}>
              {t('pause.leave')}
            </Button>
            <Button onClick={() => client.setPaused(false)}>{t('pause.resume')}</Button>
          </>
        }
      >
        <p>{t('pause.body')}</p>
      </Modal>
      <Modal
        open={leaving}
        onClose={() => {
          setLeaving(false);
          client.setPaused(false);
        }}
        title={t('leave.title')}
        actions={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setLeaving(false);
                client.setPaused(false);
              }}
            >
              {t('leave.stay')}
            </Button>
            <Button variant="danger" onClick={quit}>
              {t('leave.confirm')}
            </Button>
          </>
        }
      >
        <p>{online ? tOnline('game.leaveBody') : t('leave.body')}</p>
      </Modal>
    </div>
  );
}

/** The game route: everything from the briefing to the reveal. */
export function GameScreen() {
  const game = useGameView();
  const starting = useGameStore((s) => s.starting);
  const home = useGameStore((s) => s.home);
  const navigate = useNavigate();

  useEffect(() => {
    if (!game && !starting) void navigate(home, { replace: true });
  }, [game, starting, home, navigate]);

  if (!game) return null;
  return (
    <GameProvider client={game.client} view={game.view}>
      <GameLayout />
    </GameProvider>
  );
}
